import CheckboxGroup from '@components/checkbox';
import Panel from '@components/panel';
import { store, store$ } from '@redux/store';
import { searchParametersActions } from 'features/searchResultsFacets';
import { distinctUntilChanged, filter, map } from 'rxjs';

const container = document.querySelector('[data-schools-panel]');
const schoolListContainer = container.querySelector('[data-group]');
const labelTextContainer = container.querySelector('[data-label-text]');
const allSchoolsOption = schoolListContainer.children[0].cloneNode(true);
const schoolsSelector = (state) => state?.searchParameters?.features?.schoolDistrictIds || [];
let isAllSchoolsInactive = false;

/**
 * createOptionElement: Creates the HTML element of an school.
 * @param {Object} schoolInformation Contains the name and value of the school.
 * @returns {HTMLElement} The HTML element of the school option.
 */
function createOptionElement(schoolInformation) {
  const schoolId = `chk-${schoolInformation.Id}`;

  const optionElement = document.createElement('li');
  optionElement.classList.add('nhs-c-dropdown__option');
  optionElement.setAttribute('tabindex', 0);

  const inputElement = document.createElement('input');
  inputElement.type = 'checkbox';
  inputElement.id = schoolId;
  inputElement.value = schoolInformation.Id;
  inputElement.classList.add('nhs-c-input__check-box');
  inputElement.dataset.qa = `school-${schoolInformation.Id}`;

  const labelElement = document.createElement('label');
  labelElement.classList.add('nhs-c-dropdown__option-label');
  labelElement.setAttribute('for', schoolId);
  labelElement.innerText = schoolInformation.Name;

  optionElement.append(inputElement);
  optionElement.append(labelElement);

  return optionElement;
}

/**
 * setSchoolsList: Sets all the list of the current location.
 * @param {Array} schools List of schools.
 */
function setSchoolsList(schools) {
  schoolListContainer.innerHTML = '';
  schoolListContainer.append(allSchoolsOption);
  schools.forEach((schoolInformation) => {
    const optionElement = createOptionElement(schoolInformation);
    schoolListContainer.append(optionElement);
  });
}

/**
 * updateSelectedSchool: Updates the option selected UI of the selected schools.
 * @param {Array} selectedSchools Selected schools list
 */
function updateSelectedSchool(selectedSchools) {
  const defaultValue = allSchoolsOption.value;
  const actualDefaultOption = schoolListContainer.querySelector(`[value="${defaultValue}"]`);
  if (isAllSchoolsInactive) {
    const allOptionInputs = schoolListContainer.querySelectorAll(':checked');
    allOptionInputs.forEach((option) => {
      option.checked = false;
      option.parentElement.classList.remove('nhs-c-dropdown__option--active');
    });

    actualDefaultOption.checked = true;
    actualDefaultOption.parentElement.classList.add('nhs-c-dropdown__option--active');
    labelTextContainer.classList.remove('nhs-c-select-button--active');

    return;
  }

  actualDefaultOption.parentElement.classList.remove('nhs-c-dropdown__option--active');
  labelTextContainer.classList.add('nhs-c-select-button--active');
  selectedSchools.forEach((schoolId) => {
    const schoolOption = schoolListContainer.querySelector(`[value="${schoolId}"]`);
    schoolOption.checked = true;
    schoolOption.parentElement.classList.add('nhs-c-dropdown__option--active');
  });
  actualDefaultOption.checked = false;
}

/**
 * updateSchoolsLabel: Updates the label of the dropdown.
 * @param {Number} selectedSchoolsNumber Schools selected quantity.
 */
function updateSchoolsLabel(selectedSchoolsNumber) {
  if (!selectedSchoolsNumber) {
    labelTextContainer.innerText = labelTextContainer.dataset.defaultText;
    return;
  }

  labelTextContainer.innerText = `${selectedSchoolsNumber} Selected`;
}

(() => new Panel({ container }))();

const selectedOptions$ = new CheckboxGroup(schoolListContainer).getObservable();

/**
 * Here we manage the options selected to get only the schools id list that will be used.
 */
const selectedSchoolIds$ = selectedOptions$.pipe(
  // Here we are checking if the last option selected was All Schools.
  // If yes, we have to remove all the schools. Otherwise, we keep the same options.
  map((selectedOptions) => {
    const defaultValue = String(allSchoolsOption.value);
    const shouldClearAllSchools = !isAllSchoolsInactive && selectedOptions.includes(defaultValue);

    return selectedOptions.filter(() => !shouldClearAllSchools);
  }),

  // Here we are removing the All Schools option if it is checked.
  map((selectedOptions) => selectedOptions.filter((schoolId) => Number(schoolId) !== allSchoolsOption.value))
);

/**
 * Here we map the schools data into the dispatch object structure.
 */
selectedSchoolIds$.subscribe((schoolIds) => {
  const payload = schoolIds.map((id) => {
    const schoolOption = schoolListContainer.querySelector(`label[for="chk-${id}"]`);
    return {
      name: schoolOption.innerText,
      id,
    };
  });
  store.dispatch(searchParametersActions.fetchSchools(payload));
});

const newSchoolDistricts$ = store$.pipe(
  map((state) => state?.searchParameters?.counts?.schoolDistricts),
  filter((value) => !!value),
  distinctUntilChanged()
);

const schoolDistrictIdsChange$ = store$.pipe(
  map((state) => state?.searchParameters?.features?.schoolDistrictIds),
  filter(Boolean),
  distinctUntilChanged()
);

newSchoolDistricts$.subscribe((schools) => {
  const schoolsSelected = schoolsSelector(store.getState());
  setSchoolsList(schools);
  isAllSchoolsInactive = !Array.isArray(schoolsSelected) || !schoolsSelected.length;
  updateSelectedSchool(schoolsSelected);
  updateSchoolsLabel(schoolsSelected.length);
});
schoolDistrictIdsChange$.subscribe((selectedSchools) => {
  isAllSchoolsInactive = !Array.isArray(selectedSchools) || !selectedSchools.length;
  updateSelectedSchool(selectedSchools);
  updateSchoolsLabel(selectedSchools.length);
});
